import gql from "graphql-tag";

export const SESSION_FIELDS = gql`
  fragment AccountSessionFields on AccountSessionV2 {
    state
    verificationRequired
    customer {
      id
      firstName
      lastName
      email
      pendingEmail
      emailVerified
      phone
      newsletter
      defaultAddressId
    }
  }
`;
export const ACCOUNT_SESSION = gql`
  query AccountSession {
    accountSessionV2 {
      ...AccountSessionFields
    }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_REGISTRATION_POLICY = gql`
  query AccountRegistrationPolicy {
    accountRegistrationPolicyV2 {
      termsVersion
      termsUrl
    }
  }
`;
export const ACCOUNT_LOGIN = gql`
  mutation AccountLogin($input: AccountLoginInputV2!) {
    accountLoginV2(input: $input) {
      ok
      error {
        code
        message
        retryAfterSeconds
        fieldErrors {
          field
          code
          message
        }
      }
      session {
        ...AccountSessionFields
      }
    }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_REGISTER = gql`
  mutation AccountRegister($input: AccountRegisterInputV2!) {
    accountRegisterV2(input: $input) {
      ok
      error {
        code
        message
        retryAfterSeconds
        fieldErrors {
          field
          code
          message
        }
      }
      session {
        ...AccountSessionFields
      }
    }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_LOGOUT = gql`
  mutation AccountLogout {
    accountLogoutV2 {
      ok
      error {
        code
        message
      }
      session {
        ...AccountSessionFields
      }
    }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_VERIFY = gql`
  mutation AccountVerify($token: String!) {
    accountVerifyEmailV2(token: $token) {
      ok
      error {
        code
        message
        retryAfterSeconds
        fieldErrors {
          field
          code
          message
        }
      }
      session {
        ...AccountSessionFields
      }
    }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_RESEND = gql`
  mutation AccountResend {
    accountResendVerificationV2 {
      ok
      error {
        code
        message
        retryAfterSeconds
        fieldErrors {
          field
          code
          message
        }
      }
      session {
        ...AccountSessionFields
      }
    }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_REQUEST_RESET = gql`
  mutation AccountRequestReset($email: String!) {
    accountRequestPasswordResetV2(email: $email) {
      ok
      error {
        code
        message
        retryAfterSeconds
        fieldErrors {
          field
          code
          message
        }
      }
      session {
        ...AccountSessionFields
      }
    }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_RESET = gql`
  mutation AccountReset($input: AccountResetPasswordInputV2!) {
    accountResetPasswordV2(input: $input) {
      ok
      error {
        code
        message
        retryAfterSeconds
        fieldErrors {
          field
          code
          message
        }
      }
      session {
        ...AccountSessionFields
      }
    }
  }
  ${SESSION_FIELDS}
`;

const OPERATION_RESULT = `
  ok
  error { code message retryAfterSeconds fieldErrors { field code message } }
  session { ...AccountSessionFields }
`;

export const ACCOUNT_UPDATE_PROFILE = gql`
  mutation AccountUpdateProfile($input: AccountProfileInputV2!) {
    accountUpdateProfileV2(input: $input) { ${OPERATION_RESULT} }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_REQUEST_EMAIL_CHANGE = gql`
  mutation AccountRequestEmailChange($email: String!) {
    accountRequestEmailChangeV2(email: $email) { ${OPERATION_RESULT} }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_CANCEL_EMAIL_CHANGE = gql`
  mutation AccountCancelEmailChange {
    accountCancelEmailChangeV2 { ${OPERATION_RESULT} }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_VERIFY_EMAIL_CHANGE = gql`
  mutation AccountVerifyEmailChange($token: String!) {
    accountVerifyEmailChangeV2(token: $token) { ${OPERATION_RESULT} }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_CHANGE_PASSWORD = gql`
  mutation AccountChangePassword($currentPassword: String!, $newPassword: String!) {
    accountChangePasswordV2(currentPassword: $currentPassword, newPassword: $newPassword) { ${OPERATION_RESULT} }
  }
  ${SESSION_FIELDS}
`;
export const ACCOUNT_ADDRESSES = gql`
  query AccountAddresses {
    accountAddressesV2 {
      id
      firstName
      lastName
      company
      address1
      address2
      city
      postcode
      countryId
      countryName
      zoneId
      zoneName
      isDefault
    }
  }
`;
export const ACCOUNT_ADDRESS = gql`
  query AccountAddress($id: ID!) {
    accountAddressV2(id: $id) {
      id
      firstName
      lastName
      company
      address1
      address2
      city
      postcode
      countryId
      countryName
      zoneId
      zoneName
      isDefault
    }
  }
`;
const ADDRESS_RESULT = `ok error { code message fieldErrors { field code message } } address { id firstName lastName company address1 address2 city postcode countryId countryName zoneId zoneName isDefault } addresses { id firstName lastName company address1 address2 city postcode countryId countryName zoneId zoneName isDefault }`;
export const ACCOUNT_CREATE_ADDRESS = gql`mutation AccountCreateAddress($input: AccountAddressInputV2!) { accountCreateAddressV2(input: $input) { ${ADDRESS_RESULT} } }`;
export const ACCOUNT_UPDATE_ADDRESS = gql`mutation AccountUpdateAddress($id: ID!, $input: AccountAddressInputV2!) { accountUpdateAddressV2(id: $id, input: $input) { ${ADDRESS_RESULT} } }`;
export const ACCOUNT_DELETE_ADDRESS = gql`mutation AccountDeleteAddress($id: ID!) { accountDeleteAddressV2(id: $id) { ${ADDRESS_RESULT} } }`;
export const ACCOUNT_SET_DEFAULT_ADDRESS = gql`mutation AccountSetDefaultAddress($id: ID!) { accountSetDefaultAddressV2(id: $id) { ${ADDRESS_RESULT} } }`;

export const WISHLIST_FIELDS = gql`
  fragment AccountWishlistFields on AccountWishlistV2 {
    count
    items {
      productId
      name
      model
      href
      image
      priceRaw
      priceFormatted
      previousPriceRaw
      previousPriceFormatted
      priceChanged
      inStock
      stockStatus
      available
      minimum
      requiresOptions
      cartEligible
    }
  }
`;
export const ACCOUNT_WISHLIST = gql`
  query AccountWishlist {
    accountWishlistV2 {
      ...AccountWishlistFields
    }
  }
  ${WISHLIST_FIELDS}
`;
const WISHLIST_RESULT = `ok error { code message fieldErrors { field code message } } wishlist { ...AccountWishlistFields }`;
export const ACCOUNT_WISHLIST_ADD = gql`mutation AccountWishlistAdd($productId: ID!) { accountWishlistAddV2(productId: $productId) { ${WISHLIST_RESULT} } } ${WISHLIST_FIELDS}`;
export const ACCOUNT_WISHLIST_REMOVE = gql`mutation AccountWishlistRemove($productId: ID!) { accountWishlistRemoveV2(productId: $productId) { ${WISHLIST_RESULT} } } ${WISHLIST_FIELDS}`;
export const ACCOUNT_WISHLIST_CLEAR = gql`mutation AccountWishlistClear { accountWishlistClearV2 { ${WISHLIST_RESULT} } } ${WISHLIST_FIELDS}`;
export const ACCOUNT_WISHLIST_MERGE_GUEST = gql`mutation AccountWishlistMergeGuest { accountWishlistMergeGuestV2 { ${WISHLIST_RESULT} } } ${WISHLIST_FIELDS}`;

const ORDER_MONEY_FIELDS = `raw currency formatted`;
export const ACCOUNT_ORDERS = gql`
  query AccountOrders($page: Int!, $size: Int!) {
    accountOrdersV2(page: $page, size: $size) {
      page size totalItems totalPages
      items {
        id number placedAt status itemCount
        total { ${ORDER_MONEY_FIELDS} }
      }
    }
  }
`;
export const ACCOUNT_ORDER = gql`
  query AccountOrder($id: ID!) {
    accountOrderV2(id: $id) {
      id number placedAt status paymentMethod shippingMethod canReturn canReorder
      lines {
        orderProductId productId name model quantity productHref canReturn canReorder
        options { name value }
        unitPrice { ${ORDER_MONEY_FIELDS} }
        total { ${ORDER_MONEY_FIELDS} }
      }
      totals { title amount { ${ORDER_MONEY_FIELDS} } }
      history { status occurredAt comment }
      tracking { carrier trackingNumber trackingUrl occurredAt fallbackText }
      paymentAddress { firstName lastName company address1 address2 city postcode zone country }
      shippingAddress { firstName lastName company address1 address2 city postcode zone country }
    }
  }
`;
const RETURN_FIELDS = `id orderId orderProductId productId productName model quantity opened reason comment status createdAt`;
export const ACCOUNT_RETURN_CONTEXT = gql`
  query AccountReturnContext($orderId: ID!) {
    accountReturnReasonsV2 { id name }
    accountReturnsV2(orderId: $orderId) { ${RETURN_FIELDS} }
    accountReturnEligibilityV2(orderId: $orderId) {
      orderId eligible requestable code message policyDays deliveryAt deadline timeGateVerified manualReview
      lines { orderProductId productId name purchasedQuantity remainingQuantity eligible requestable code message }
    }
  }
`;
export const ACCOUNT_CREATE_RETURN_REQUEST = gql`
  mutation AccountCreateReturnRequest($input: AccountCreateReturnRequestInputV2!) {
    accountCreateReturnRequestV2(input: $input) {
      ok manualReview policyDays
      error { code message fieldErrors { field code message } }
      returnRequest { ${RETURN_FIELDS} }
      returns { ${RETURN_FIELDS} }
    }
  }
`;
export const ACCOUNT_REORDER_SAFE = gql`
  mutation AccountReorderSafe($input: AccountReorderInputV2!) {
    accountReorderSafeV2(input: $input) {
      ok
      addedCount
      cartItemCount
      partial
      error {
        code
        message
        fieldErrors {
          field
          code
          message
        }
      }
      lines {
        orderProductId
        productId
        quantity
        added
        code
        message
      }
    }
  }
`;
export const LEGACY_WISHLIST = gql`
  query LegacyWishlist {
    wishlist {
      id
      name
      model
      price
      image
      imageLazy
      url(url: "/store/product/_id")
      stock
      minimum
      options {
        id
        name
        type
      }
    }
  }
`;
export const LEGACY_WISHLIST_ADD = gql`
  mutation LegacyWishlistAdd($id: Int) {
    addToWishlist(id: $id) {
      id
      name
      model
      price
      image
      imageLazy
      url(url: "/store/product/_id")
      stock
      minimum
      options {
        id
        name
        type
      }
    }
  }
`;
export const LEGACY_WISHLIST_REMOVE = gql`
  mutation LegacyWishlistRemove($id: String) {
    removeWishlist(id: $id) {
      id
      name
      model
      price
      image
      imageLazy
      url(url: "/store/product/_id")
      stock
      minimum
      options {
        id
        name
        type
      }
    }
  }
`;
